import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { inputCls, labelCls } from "@/components/common/states";
import LeadForm from "@/components/common/LeadForm";
import { trackEvent } from "@/lib/tracking";
import { ArrowRight, Sparkles } from "lucide-react";

const PLACEHOLDER = "Tell us what you're looking for — e.g. 3 BHK in Baner or Wakad, budget ₹1.5 Cr, possession 2028, investment or self-use...";

// Two-step, requirement-first lead flow. Step 1: free-text requirement. Step 2: Name + Mobile.
// Reuses the existing lead pipeline (LeadForm -> /requirements) and redirects to /thank-you.
export default function RequirementFlow({ open, onOpenChange, ctaSource = "hero" }) {
  const [step, setStep] = useState(1);
  const [requirement, setRequirement] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1); setRequirement(""); setTouched(false);
      trackEvent("requirement_started", { cta_source: ctaSource });
    }
  }, [open, ctaSource]);

  const next = () => {
    if (requirement.trim().length < 6) { setTouched(true); return; }
    trackEvent("requirement_written", { cta_source: ctaSource });
    setStep(2);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="requirement-flow-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-accent" aria-hidden /> Tell us what you're looking for
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Write your requirement in your own words. We'll identify relevant upcoming opportunities in Pune."
              : "Almost done — where should our advisor reach you?"}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4">
            <div>
              <label className={labelCls} htmlFor="req-text">Your requirement</label>
              <Textarea id="req-text" data-testid="requirement-text" value={requirement}
                onChange={(e) => setRequirement(e.target.value)} rows={5} placeholder={PLACEHOLDER}
                className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-primary focus:border-primary" />
              {touched && requirement.trim().length < 6 && (
                <p className="text-sm text-destructive mt-1" data-testid="requirement-error">Please describe what you're looking for.</p>
              )}
            </div>
            <Button data-testid="requirement-continue" onClick={next}
              className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md text-sm font-semibold">
              Continue <ArrowRight className="h-4 w-4 ml-1" aria-hidden />
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-md bg-muted px-4 py-3 text-sm text-foreground/80" data-testid="requirement-recap">
              <span className="font-medium text-foreground">Your requirement:</span> {requirement}
            </div>
            <LeadForm
              source="requirement"
              variant="minimal"
              submitLabel="Submit"
              defaults={{ requirement, form_type: "homepage_requirement", cta_source: ctaSource }}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Small helper button that opens the flow — keeps hero markup clean.
export function RequirementTrigger({ className, children, ctaSource = "hero", testId = "open-requirement-flow" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid={testId} onClick={() => { setOpen(true); trackEvent("find_property_click", { cta_source: ctaSource }); }} className={className}>
        {children}
      </button>
      <RequirementFlow open={open} onOpenChange={setOpen} ctaSource={ctaSource} />
    </>
  );
}
